<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-rulers me-2"></i>Units Management
      </h4>
      <div class="d-flex gap-3">
        <button class="btn btn-outline-secondary" @click="$router.go(-1)">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
        <button v-if="profile.role !== 'user'" class="btn btn-primary" @click="unitModal = true; unitMode = 'add'">
          <i class="bi bi-plus-circle me-1"></i> Add Unit
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <!-- Loading Spinner -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading units...</p>
      </div>

      <!-- Units Content -->
      <div v-if="!loading" class="units-content fade-in">
        <!-- Units Count & Search -->
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ units.total }}</span>
            <span class="text-muted">Units</span>
          </div>
          <div class="d-flex gap-2 align-items-center">
            <UInput v-model="dataTableSearch" size="md" placeholder="Search unit" class="mb-0 w-auto" />
            <button class="btn btn-primary" @click="onSearchClick">
              <i class="bi bi-search me-1"></i> Search
            </button>
            <button class="btn btn-outline-secondary" @click="onClearSearchClick" :disabled="!dataTableSearch || !dataTableSearch.length">
              <i class="bi bi-x-circle me-1"></i> Clear
            </button>
          </div>
        </div>

        <!-- Units Table -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
              <tr>
                <th>NAME</th>
                <th>DESCRIPTION</th>
                <th>CREATED</th>
                <th class="text-end">ACTIONS</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(unit, index) in units.data" :key="unit.id" class="unit-row" :class="{'bg-light': index % 2 === 0}">
                <td>
                  <div class="d-flex align-items-center">
                    <div class="unit-avatar me-2" :class="`bg-${['primary', 'success', 'info', 'warning'][index % 4]}-light`">
                      <span class="avatar-text">{{ unit.name.charAt(0).toUpperCase() }}</span>
                    </div>
                    <span class="fw-medium">{{ unit.name }}</span>
                  </div>
                </td>
                <td>{{ unit.description }}</td>
                <td>{{ formatDate(unit.created_at) }}</td>
                <td class="text-end">
                  <button
                      v-if="profile.role !== 'unit'"
                      class="btn btn-sm btn-outline-primary"
                      @click="unitModal = true; unitMode = 'edit'; populateUnitEdit(unit)"
                  >
                    <i class="bi bi-pencil-square"></i> Edit
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage" data="">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              v-model="dataTablePage"
              :pages="totalPages"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <!-- Back Button -->
        <div class="d-flex justify-content-center mt-4">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Unit Modal -->
    <Modal :open="unitModal" @closed="unitModal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="bi" :class="unitMode === 'add' ? 'bi-plus-circle' : 'bi-pencil-square'"></i>
          {{ unitMode === 'add' ? 'Add New Unit' : 'Edit Unit' }}
        </h5>
        <button @click="unitModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row g-3">
            <div class="form-group col-md-6">
              <label for="name" class="form-label">
                <i class="bi bi-tag me-1"></i>Unit Name
              </label>
              <input
                type="text"
                id="name"
                class="form-control"
                placeholder="Enter unit name"
                v-model="unit.name"
              >
            </div>
            <div class="form-group col-md-6">
              <label for="description" class="form-label">
                <i class="bi bi-card-text me-1"></i>Description <span class="text-muted fst-italic small">(optional)</span>
              </label>
              <input
                type="text"
                id="description"
                class="form-control"
                placeholder="Enter description"
                v-model="unit.description"
              >
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="unitModal = false">
          <i class="bi bi-x-circle me-1"></i>Cancel
        </button>
        <button
            type="button"
            class="btn btn-primary"
            v-if="unitMode === 'add'"
            @click="addUnit"
        >
          <i class="bi bi-plus-circle me-1"></i>Create Unit
        </button>
        <button
            type="button"
            class="btn btn-primary"
            v-else
            @click="updateUnit(unit.id)"
        >
          <i class="bi bi-check-circle me-1"></i>Update Unit
        </button>
      </div>
    </Modal>
    <!-- Add/Edit Unit Modal End -->
  </Card>
</template>

<script>
import unitService from "@/api/unitService";
import formatDate from "../../utils/formatDate";
import {decrypt} from "@/utils/crypto";
import Pagination from "@/components/pagination/Pagination.vue";

export default {
  name: "Units",

  components: {
    Pagination
  },

  data(){
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    return {
      profile: {},
      organization: {},
      loading: true,
      units: {
        data: []
      },
      unitMode: 'add',
      unitModal: false,
      dataTableSearch: this.$route?.query?.search ?? '',
      dataTablePerPage: this.$route?.query?.per_page ? parseInt(this.$route.query.per_page) : 10,
      dataTablePage: this.$route?.query?.page ? parseInt(this.$route.query.page) : 1,
      unit: {
        organization_id: this.organization.id,
        name: '',
        description: '',
      },
    }
  },

  methods: {
    formatDate,
    getUnits: async function () {
      this.loading = true;
      const params = {
        page: this.dataTablePage,
        per_page: this.dataTablePerPage,
        search: this.dataTableSearch?.toString().trim() || undefined,
      }
      try {
        this.units = await unitService.getUnits(params);
      } catch (error) {
        console.error("Error fetching units:", error);
      } finally {
        this.loading = false;
      }
    },
    onSearchClick(){
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getUnits()
    },
    onClearSearchClick(){
      if (!this.dataTableSearch) return;
      this.dataTableSearch = ''
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getUnits()
    },
    updateRouteQuery() {
      const query = {
        ...this.$route.query,
        page: this.dataTablePage !== 1 ? this.dataTablePage : undefined,
        per_page: this.dataTablePerPage !== 10 ? this.dataTablePerPage : undefined,
        search: this.dataTableSearch && this.dataTableSearch.trim().length ? this.dataTableSearch.trim() : undefined,
      }
      Object.keys(query).forEach(k => query[k] === undefined && delete query[k])
      this.$router.replace({ path: this.$route.path, query })
    },
    populateUnitEdit(unit) {
      // Create a deep copy to avoid direct reference modification
      this.unit = JSON.parse(JSON.stringify(unit));
    },
    async addUnit() {
      try {
        let response = await unitService.createUnit(this.unit);
        if (response) {
          this.unitModal = false;
          await this.getUnits();
          this.resetUnitForm();
        }
      } catch (error) {
        console.error("Error adding unit:", error);
      }
    },
    async updateUnit(id) {
      try {
        let response = await unitService.updateUnit(this.unit, id);
        if (response) {
          this.unitModal = false;
          await this.getUnits();
        }
      } catch (error) {
        console.error("Error updating unit:", error);
      }
    },
    resetUnitForm() {
      // Reset the unit form to default values
      this.unit = {
        organization_id: this.organization.id,
        name: '',
        description: '',
      };
    }
  },

  created: async function () {
    try {
      this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')));
      this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
      await this.getUnits();
    } catch (error) {
      console.error("Error in created lifecycle hook:", error);
      this.loading = false;
    }
  },

  computed: {
    totalPages(){
      return this.units?.last_page || 1
    }
  },

  watch: {
    unitModal: function () {
      if (!this.unitModal && this.unitMode === 'add') {
        this.resetUnitForm();
      }
    },
    dataTablePerPage(){
      this.dataTablePage = 1;
      this.updateRouteQuery()
      this.getUnits()
    },
    dataTablePage(){
      this.updateRouteQuery()
      this.getUnits()
    },
  }
}
</script>

<style scoped>
/* Units page styles */
.units-content {
  padding: 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.unit-row {
  transition: all 0.2s ease;
}

.unit-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* Unit avatar */
.unit-avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.9rem;
}

.avatar-text {
  color: inherit;
}

/* Background colors for avatars */
.bg-primary-light {
  background-color: #eef2ff;
  color: #4361ee;
}

.bg-success-light {
  background-color: #d1fae5;
  color: #10b981;
}

.bg-info-light {
  background-color: #dbeafe;
  color: #3b82f6;
}

.bg-warning-light {
  background-color: #fef3c7;
  color: #f59e0b;
}

/* Badge styles */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table th,
  .table td {
    padding: 0.75rem 1rem;
  }

  .unit-avatar {
    width: 32px;
    height: 32px;
    font-size: 0.8rem;
  }
}
</style>
